/**
 * SystemNotificationBanner Component
 *
 * Floating card pinned to the bottom-left of the screen.
 * Shows the first active system notification for the current user.
 * Lightweight, on-brand (teal accent), supports dark mode.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOverlayOpen } from '@/hooks/useOverlayOpen';
import { usePublishFloatingHeight } from '@/hooks/usePublishFloatingHeight';
import { useSystemNotifications } from '@/hooks/useSystemNotifications';
import { NotificationSeverity, NotificationType } from '@/services/system-notifications.service';

// ─── Accent helpers ─────────────────────────────────────────────────────────

interface AccentClasses {
  /** Left bar background, e.g. "bg-primary-500" */
  bar: string;
  /** Icon container background + text, e.g. "bg-primary-50 text-primary-600" */
  iconWrap: string;
  /** Dark-mode icon container overrides */
  iconWrapDark: string;
}

/**
 * Returns lightweight accent classes (left bar + icon tint) per type/severity.
 * No full-bleed fills — only the accent strip and icon container are tinted.
 */
const getAccent = (type: NotificationType, severity: NotificationSeverity): AccentClasses => {
  if (type === 'MAINTENANCE') {
    return {
      bar: 'bg-slate-500',
      iconWrap: 'bg-slate-100 text-slate-600',
      iconWrapDark: 'dark:bg-slate-700 dark:text-slate-300',
    };
  }
  if (type === 'FEATURE') {
    return {
      bar: 'bg-primary-500',
      iconWrap: 'bg-primary-50 text-primary-600',
      iconWrapDark: 'dark:bg-primary-900/40 dark:text-primary-300',
    };
  }
  if (type === 'PROMOTION') {
    return {
      bar: 'bg-emerald-500',
      iconWrap: 'bg-emerald-50 text-emerald-600',
      iconWrapDark: 'dark:bg-emerald-900/40 dark:text-emerald-300',
    };
  }
  if (type === 'ANNOUNCEMENT') {
    return {
      bar: 'bg-primary-500',
      iconWrap: 'bg-primary-50 text-primary-600',
      iconWrapDark: 'dark:bg-primary-900/40 dark:text-primary-300',
    };
  }

  // Severity-based fallback
  switch (severity) {
    case 'ERROR':
      return {
        bar: 'bg-red-500',
        iconWrap: 'bg-red-50 text-red-600',
        iconWrapDark: 'dark:bg-red-900/40 dark:text-red-300',
      };
    case 'WARNING':
      return {
        bar: 'bg-amber-400',
        iconWrap: 'bg-amber-50 text-amber-600',
        iconWrapDark: 'dark:bg-amber-900/40 dark:text-amber-300',
      };
    case 'SUCCESS':
      return {
        bar: 'bg-emerald-500',
        iconWrap: 'bg-emerald-50 text-emerald-600',
        iconWrapDark: 'dark:bg-emerald-900/40 dark:text-emerald-300',
      };
    default:
      // INFO / unknown
      return {
        bar: 'bg-primary-500',
        iconWrap: 'bg-primary-50 text-primary-600',
        iconWrapDark: 'dark:bg-primary-900/40 dark:text-primary-300',
      };
  }
};

// ─── Icon ────────────────────────────────────────────────────────────────────

/**
 * Icon SVG based on notification type / severity (unchanged shapes from v1).
 */
const getIcon = (type: NotificationType, severity: NotificationSeverity) => {
  if (type === 'MAINTENANCE') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    );
  }
  if (type === 'FEATURE') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    );
  }
  if (type === 'PROMOTION') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
      </svg>
    );
  }
  if (type === 'ANNOUNCEMENT') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    );
  }

  // Severity-based icons
  if (severity === 'ERROR') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    );
  }
  if (severity === 'WARNING') {
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }

  // Default info icon
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

// ─── Utility ─────────────────────────────────────────────────────────────────

const isExternalUrl = (url: string): boolean => url.includes('://');

// ─── Component ───────────────────────────────────────────────────────────────

export const SystemNotificationBanner = () => {
  const { t } = useTranslation('common');
  const { undismissedNotifications, dismiss } = useSystemNotifications();
  const [isExpanded, setIsExpanded] = useState(false);
  const overlayOpen = useOverlayOpen();
  const [bannerEl, setBannerEl] = useState<HTMLDivElement | null>(null);

  // Publish the banner's height so bottom-right widgets stack above it on mobile (index.css).
  usePublishFloatingHeight(bannerEl, '--floating-banner-h', 8);

  // Reset expanded state when notification changes
  useEffect(() => {
    setIsExpanded(false);
  }, [undismissedNotifications[0]?.id]);

  if (undismissedNotifications.length === 0 || overlayOpen) {
    return null;
  }

  const notification = undismissedNotifications[0];
  const accent = getAccent(notification.type, notification.severity);
  const showCta = notification.ctaText && notification.ctaUrl;
  const ctaIsExternal = notification.ctaUrl ? isExternalUrl(notification.ctaUrl) : false;

  return (
    <div
      ref={setBannerEl}
      className={[
        // Position: bottom-left floating card
        'fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+10px+var(--floating-install-h))] lg:bottom-4 left-4 z-40',
        // On small screens use left+right padding so it doesn't overflow
        'right-4 sm:right-auto',
        // Card width on sm+
        'sm:max-w-md',
        // Light card surface
        'bg-white dark:bg-slate-800',
        // Rounded card
        'rounded-2xl',
        // Border
        'border border-gray-200/70 dark:border-white/10',
        // Shadow — elevated but not heavy
        'shadow-xl',
        // Overflow hidden so the left accent bar clips to the card radius
        'overflow-hidden',
      ].join(' ')}
      role="alert"
      aria-live="polite"
      style={{
        animation: 'notificationSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <style>
        {`
          @keyframes notificationSlideUp {
            from { transform: translateY(100%); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
          }
        `}
      </style>

      {/* Layout: left accent bar + body */}
      <div className="flex">

        {/* Left accent bar */}
        <div className={`w-1 flex-shrink-0 ${accent.bar}`} aria-hidden="true" />

        {/* Card body */}
        <div className="flex-1 p-4 min-w-0">

          {/* Top row: icon + title + NEW badge + dismiss */}
          <div className="flex items-start gap-3">

            {/* Icon container — tinted circle */}
            <div
              className={[
                'flex-shrink-0 flex items-center justify-center',
                'w-8 h-8 rounded-full',
                accent.iconWrap,
                accent.iconWrapDark,
              ].join(' ')}
              aria-hidden="true"
            >
              {getIcon(notification.type, notification.severity)}
            </div>

            {/* Title + message */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-gray-900 dark:text-white leading-snug">
                  {notification.title}
                </span>
                {/* eslint-disable i18next/no-literal-string */}
                {notification.type === 'FEATURE' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300">
                    NEW
                  </span>
                )}
                {/* eslint-enable i18next/no-literal-string */}
              </div>

              <p
                className={[
                  'text-sm text-gray-800 dark:text-gray-200 mt-1 whitespace-pre-line',
                  isExpanded ? '' : 'line-clamp-2',
                ].join(' ')}
              >
                {notification.message}
              </p>

              {/* Read more / Show less toggle */}
              {notification.message.length > 100 && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-1 underline underline-offset-2 transition-colors"
                >
                  {isExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>

            {/* Dismiss button — top-right of card */}
            {notification.isDismissible && (
              <button
                onClick={() => dismiss(notification.id)}
                className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                aria-label={t('dismiss_notification', { defaultValue: 'Dismiss notification' })}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* CTA button — below message, left-aligned */}
          {showCta && (
            <div className="mt-3">
              <a
                href={notification.ctaUrl!}
                target={ctaIsExternal ? '_blank' : undefined}
                rel={ctaIsExternal ? 'noopener noreferrer' : undefined}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-600 text-primary-600-contrast text-xs font-semibold hover:bg-primary-700 active:bg-primary-800 transition-colors duration-150 shadow-sm"
              >
                {notification.ctaText}
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SystemNotificationBanner;
